/**
 * Behavioral helpers used by MAIN_STEPS `renderProps` callbacks.
 *
 * - `getWrapperProps`  – extracts common OnboardingStepWrapper props from context
 * - `bankingSignupOnboardingBase` / `bankingSignupActivityFields` – build PATCH bodies
 * - `persistOnboardingThenContinue` – PATCH with deduplication
 */
import type {
  AccountableOnboardingBody,
  AccountableOnboardingResponse,
} from 'api/v2/transactions/patchAccountableBankingOnboarding';
import type { BankingSignupAccountDetails } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';
import type { StepRenderContext } from './stepDefinitions.types';

/**
 * Extracts common wrapper props from context.
 * Use via spread in renderProps: `...getWrapperProps(ctx)`
 */
export const getWrapperProps = (ctx: StepRenderContext) => ({
  progressSegments: ctx.progressSegments,
  onProgressSegmentClick: ctx.onProgressSegmentClick,
  title: ctx.title,
  subtitle: ctx.subtitle,
  icon: ctx.icon,
  isUpdatingStep: ctx.isUpdatingStep,
  showBackButton: true,
  onBack: ctx.onBack,
});

/** Shared PATCH body for incremental banking signup saves (personal block). */
export function bankingSignupOnboardingBase(
  ctx: StepRenderContext,
  values: BankingSignupAccountDetails,
): AccountableOnboardingBody {
  return {
    ...(ctx.onboardingId ? { _id: ctx.onboardingId } : {}),
    userId: ctx.user?._id,
    country: ctx.user?.country,
    personalDetails: {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    },
  };
}

/** Activity / company fields shared by the final personal-details substeps. */
export function bankingSignupActivityFields(
  ctx: StepRenderContext,
  values: BankingSignupAccountDetails,
): Pick<
  AccountableOnboardingBody,
  | 'businessActivity'
  | 'businessActivityDescription'
  | 'monthlyPaymentVolume'
  | 'companyName'
  | 'additionalBusinessActivityDescription'
> {
  return {
    businessActivity: values?.APC1,
    businessActivityDescription: ctx.t(
      `${ctx.user?.country}.APC2.${values?.APC2}`,
    ),
    monthlyPaymentVolume: values?.monthlyPaymentVolume,
    companyName: values?.companyName,
    additionalBusinessActivityDescription:
      values?.additionalBusinessActivityDescription,
  };
}

/**
 * PATCH onboarding then advance the step. Skips the HTTP call when the payload (without `_id`)
 * matches the last successful save (`lastPersistedPayload`), and only calls `onContinue()`.
 *
 * **Why `_id` is excluded from comparison:** After the first create, bodies include `_id` from
 * context; comparing without `_id` means we dedupe on "user-visible" fields, not on whether
 * the draft id is now known.
 *
 * **Global cache, not per step:** The snapshot is a single string for the session. If the user
 * was on a later step (large body, e.g. `furthestStepCompleted`) and navigates back to an
 * earlier substep, the first Continue sends a smaller/different body → hash mismatch → one real
 * PATCH. After success, the snapshot matches that substep; repeating Continue with unchanged
 * data skips the network. Going back does not reset the snapshot.
 *
 * **Failures:** `lastPersistedPayload` is updated only in `onSuccess`. A failed PATCH leaves
 * the old snapshot so the next attempt still sends the request.
 */
export function persistOnboardingThenContinue(
  ctx: StepRenderContext,
  body: AccountableOnboardingBody,
) {
  const { _id, ...payload } = body;
  const serialized = JSON.stringify(payload);

  if (ctx.lastPersistedPayload === serialized) {
    ctx.onContinue();
    return;
  }

  return ctx.onboardingAPI.mutate(body, {
    onSuccess: (data: AccountableOnboardingResponse) => {
      ctx.setLastPersistedPayload(serialized);
      ctx.setOnboardingId(data._id);
      ctx.onContinue();
    },
  });
}
